/**
 * Cookie-session support for KeyNet single sign-on.
 *
 * The session itself lives in an HttpOnly cookie that page scripts cannot
 * read. Requests only need `withCredentials` so the browser sends it, plus
 * the CSRF token on state-changing requests. The CSRF token is kept in
 * memory only, never in localStorage or sessionStorage.
 */
import axios, { AxiosInstance } from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const UNSAFE_METHODS = new Set(['post', 'put', 'patch', 'delete']);

let csrfToken: string | null = null;

export async function loadCsrfToken(): Promise<void> {
  try {
    const response = await axios.get(`${API_BASE_URL}/auth/csrf`, { withCredentials: true });
    csrfToken = response.data?.csrf_token ?? null;
  } catch {
    csrfToken = null;
  }
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

export function clearCsrfToken(): void {
  csrfToken = null;
}

/** Send the session cookie on every request and the CSRF token on unsafe ones. */
export function attachSessionSupport(instance: AxiosInstance): void {
  instance.defaults.withCredentials = true;
  instance.interceptors.request.use((config) => {
    const method = (config.method || 'get').toLowerCase();
    if (csrfToken && UNSAFE_METHODS.has(method)) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }
    return config;
  });
}

/** Where to send a visitor without a session: their KeyNet, via the backend. */
export function keynetStartUrl(returnTo: string = '/'): string {
  const account = process.env.REACT_APP_SSO_ACCOUNT || '';
  return (
    `${API_BASE_URL}/auth/keynet/start?account=${encodeURIComponent(account)}` +
    `&return_to=${encodeURIComponent(returnTo)}`
  );
}

export function isSsoEnabled(): boolean {
  return Boolean(process.env.REACT_APP_SSO_ACCOUNT);
}
